import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";

// =======================================================
// Helpers
// =======================================================
function parseFirestoreDate(value) {
  if (!value) return null;

  // Firestore Timestamp (toDate)
  if (value?.toDate && typeof value.toDate === "function") {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }

  // Timestamp no formato { seconds }
  if (typeof value === "object" && value.seconds) {
    const d = new Date(value.seconds * 1000);
    return isNaN(d.getTime()) ? null : d;
  }

  // Epoch number
  if (typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  // ISO string / string parseável
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  // Já é Date
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  return null;
}

function pickUserCreatedAt(userData) {
  // tenta em ordem de prioridade (ajuste se quiser)
  return (
    parseFirestoreDate(userData?.createdAt) ||
    parseFirestoreDate(userData?.created_at) ||
    parseFirestoreDate(userData?.signupDate) ||
    parseFirestoreDate(userData?.dateCreated) ||
    null
  );
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function monthKey(date) {
  // YYYY-MM
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

function monthLabel(date) {
  // "Nov/25", "Dez/25" (pt-BR)
  const m = date
    .toLocaleDateString("pt-BR", { month: "short" })
    .replace(".", ""); // remove ponto (ex: "nov.")
  const yy = String(date.getFullYear()).slice(-2);
  return `${m.charAt(0).toUpperCase() + m.slice(1)}/${yy}`;
}

function buildLastNMonths(n = 12) {
  const now = new Date();
  const months = [];

  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: monthKey(d),
      label: monthLabel(d),
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
    });
  }

  return months;
}

function UsersGrowthChart() {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const last12 = useMemo(() => buildLastNMonths(12), []);

  // =======================================================
  // 🔥 Carregar dados REAIS do Firestore (correto por mês+ano)
  // =======================================================
  useEffect(() => {
    let mounted = true;

    async function loadUserGrowth() {
      try {
        const snap = await getDocs(collection(db, "users"));

        // contador por YYYY-MM
        const counts = {};
        last12.forEach((m) => (counts[m.key] = 0));

        let counted = 0;
        let ignored = 0;

        snap.forEach((doc) => {
          const u = doc.data();
          const createdAt = pickUserCreatedAt(u);

          if (!createdAt) {
            ignored++;
            return;
          }

          const key = monthKey(createdAt);

          // Só conta se estiver dentro da janela (últimos 12)
          if (counts[key] !== undefined) {
            counts[key] += 1;
            counted++;
          }
        });

        const formatted = last12.map((m) => ({
          month: m.label,
          users: counts[m.key] ?? 0,
        }));

        if (mounted) {
          setChartData(formatted);

          // Debug simples (pode remover depois)
          // console.log("UsersGrowthChart:", { counted, ignored, counts, formatted });
        }
      } catch (error) {
        console.error("Erro ao carregar crescimento de usuários:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadUserGrowth();

    return () => {
      mounted = false;
    };
  }, [last12]);

  // =======================================================
  // LOADING
  // =======================================================
  if (loading) {
    return (
      <div className="chartBox" style={{ minHeight: 360 }}>
        <h3>Evolução de usuários 💗</h3>
        <div className="chart-loading">Carregando gráfico...</div>
      </div>
    );
  }

  // =======================================================
  // RENDER FINAL
  // =======================================================
  return (
    <div className="chartBox" style={{ minHeight: 360 }}>
      <h3>Evolução de usuários 💗</h3>

      {/* garante altura do gráfico */}
      <div className="chart-container" style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

            <XAxis dataKey="month" stroke="#888" fontSize={12} />
            <YAxis allowDecimals={false} stroke="#888" fontSize={12} />

            <Tooltip
              formatter={(value) => [`${value} usuários`, "Novos"]}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ff4f9a",
                borderRadius: "8px",
                fontSize: "12px",
              }}
            />

            <Line
              type="monotone"
              dataKey="users"
              stroke="#ff4f9a"
              strokeWidth={3}
              dot={{ fill: "#ff4f9a", strokeWidth: 2, r: 5 }}
              activeDot={{ r: 8, fill: "#ff4f9a" }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default UsersGrowthChart;
